import {
  isFuture, format, formatDistanceToNow,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import React, { useContext, useEffect, useState } from 'react';
import {
  Card, Chip, IconButton, useTheme, Text, Divider, List, Avatar, Button, Dialog, Portal,
} from 'react-native-paper';
import Animated, {
  Easing,
  interpolateColor, useAnimatedStyle, useSharedValue, withRepeat, withSequence, withTiming,
} from 'react-native-reanimated';
import { useToast } from 'native-base';
import { useNavigation } from '@react-navigation/native';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import Flag from 'react-native-flags';
import {
  TouchableOpacity, StyleSheet, View,
} from 'react-native';
import { FindManyEvents } from '../../../gql/event/query';
import { EVENT_PARTICIPANT_MODE, SCREEN_NAMES } from '../../../utils/constant';
import RankIcon from '../../../components/commons/RankIcon';
import { ModalContext } from '../../../context/ModalContext';
import { JoinEvent, LeaveEvent } from '../../../gql/event/mutation';
import { FindManyAchievementsForOneEvent } from '../../../gql/achievement/query';
import { ISOCODE } from '../../../utils/isocode';
import useDesktopLayout from '../../../hooks/useDesktopLayout';
import WeightTrackerModal from '../../../modals/ManageWeights';

function EventCard({
  user, event, isSuperAdmin, isAdmin, handlePlayersOnEvent,
}) {
  const {
    togglePictureModal,
    displayWeightModal,
    toggleWeightModal,
  } = useContext(ModalContext);
  const toast = useToast();
  const navigation = useNavigation();
  const [joinEvent] = useMutation(JoinEvent);
  const [leaveEvent] = useMutation(LeaveEvent);
  const theme = useTheme();
  const isDesktop = useDesktopLayout();

  const [expanded, setExpanded] = useState(false);
  const [blockersVisible, setBlockersVisible] = useState(false);
  const [weightRequested, setWeightRequested] = useState(false);
  const apolloClient = useApolloClient();

  const { data: achievementsData } = useQuery(FindManyAchievementsForOneEvent, {
    variables: { id: Number(event.id) },
    skip: event.status !== 'CLOSED' || !expanded,
  });

  const progress = useSharedValue(0);

  const handlePress = () => setExpanded(!expanded);

  const registrationWarning = useAnimatedStyle(() => {
    const warningColor = theme.colors.warning || '#ffeb3b'; // Fallback color
    return {
      borderColor: interpolateColor(
        progress.value,
        [0, 1],
        [warningColor, 'transparent'],
        'RGB',
      ),
      borderWidth: 1, // Constant, visible border
    };
  });

  useEffect(() => {
    if (event.lessThan14days && isFuture(new Date(event.registerEndAt))) {
      progress.value = withRepeat(withSequence(withTiming(1, {
        duration: 1500,
        easing: Easing.inOut(Easing.ease),
      }), withTiming(0, {
        duration: 1500,
        easing: Easing.inOut(Easing.ease),
      })), -1, true);
    } else {
      progress.value = withTiming(0);
    }
  }, [event.lessThan14days, event.registerEndAt]);

  useEffect(() => {
    if (weightRequested && !displayWeightModal.display) {
      setWeightRequested(false);
      apolloClient.refetchQueries({ include: [FindManyEvents] });
    }
  }, [displayWeightModal.display]);

  const splittedLocation = event.location.split(',');
  const country = splittedLocation[splittedLocation.length - 1].trim();
  const countryCode = Object.entries(ISOCODE).find((e) => e[1] === country)?.[0];

  const isRegistrationClosed = !isFuture(new Date(event.registerEndAt));
  const canStillRegister = isFuture(new Date(event.registerEndAt)) && !event.isTerminated;

  // Reasons the current user can't register: shown inline, details in a dialog.
  const blockers = event.hasAlreadyJoined || event.isTerminated || isRegistrationClosed
    || user?.status === 'COMPANION' ? [] : (event.registrationBlockers ?? []);
  const blockerActions = new Set(blockers.map((b) => b.action).filter(Boolean));
  const goToProfile = (tabIndex) => {
    setBlockersVisible(false);
    navigation.navigate({
      name: SCREEN_NAMES.PROFILE,
      params: { email: user.email, tabIndex },
    });
  };

  const player = event.players?.find((p) => p.player.id === user?.id);
  const isUserPending = player && !player?.isConfirmed;
  const isUserConfirmed = player && player?.isConfirmed;

  const renderStatusChip = () => {
    if (event.isTerminated) {
      return <Chip icon="cancel" style={styles.statusChip} theme={{ colors: { primary: theme.colors.error } }}>Terminé</Chip>;
    }
    if (isRegistrationClosed) {
      return <Chip icon="lock-outline" style={styles.statusChip} theme={{ colors: { primary: theme.colors.onSurfaceDisabled } }}>Inscriptions fermées</Chip>;
    }
    if (event.lessThan14days) {
      return (
        <Chip icon="alert-circle-outline" style={styles.statusChip} theme={{ colors: { primary: theme.colors.warning } }}>
          {`Fin des inscriptions ${formatDistanceToNow(new Date(event.registerEndAt), { addSuffix: true, locale: fr })}`}
        </Chip>
      );
    }
    return (
      <Chip icon="lock" style={styles.statusChip} theme={{ colors: { primary: theme.colors.error } }}>
        {event.eventSubtitle}
      </Chip>
    );
  };

  const renderMainAction = () => {
    if (event.status === 'CLOSED') {
      return (
        <List.Accordion
          title="Voir les résultats"
          left={(props) => <List.Icon {...props} icon="podium" />}
          expanded={expanded}
          onPress={handlePress}
          style={[styles.mainActionButton, { backgroundColor: theme.colors.primary }]}
          titleStyle={{ color: 'white' }}
          theme={{ colors: { primary: 'white' } }}
        >
          {achievementsData?.findManyAchievementsForOneEvent?.length > 0 ? (
            achievementsData.findManyAchievementsForOneEvent.map((achievement, index) => (
              <List.Item
                key={achievement.id}
                title={`${achievement.playerOnEvent.player.firstName} ${achievement.playerOnEvent.player.lastName}`}
                description={() => (
                  <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip style={{ marginHorizontal: 1, marginVertical: 2 }}>
                      {achievement.category}
                    </Chip>
                    {achievement.weightCategory && (
                      <Chip style={{ marginHorizontal: 1, marginVertical: 2 }}>
                        {achievement.weightCategory}
                        {' '}
                        kg
                      </Chip>
                    )}
                    <Chip style={{ marginHorizontal: 1, marginVertical: 2 }}>
                      {
                        Object.entries(EVENT_PARTICIPANT_MODE).find(
                          (item) => item[0] === achievement.mode,
                        )[1]
                      }
                    </Chip>
                  </View>
                )}
                left={(props) => <RankIcon rank={achievement.rank} {...props} />}
                right={() => (
                  achievement.playerOnEvent.player.profile.pictureThumbnail
                    ? (
                      <Avatar.Image
                        size={40}
                        source={{ uri: achievement.playerOnEvent.player.profile.pictureThumbnail }}
                      />
                    )
                    : (
                      <Avatar.Text
                        size={40}
                        label={`${achievement.playerOnEvent.player.firstName[0]}${achievement.playerOnEvent.player.lastName[0]}`}
                      />
                    )
                )}
                style={{ backgroundColor: 'white' }}
              />
            ))
          ) : (
            <List.Item title="Aucun résultat disponible" style={{ backgroundColor: 'white' }} />
          )}
        </List.Accordion>
      );
    }
    if (event.hasAlreadyJoined
      && isUserPending) {
      return (
        <TouchableOpacity
          style={[styles.mainActionButton, { backgroundColor: theme.colors.error }]} // Use error color for 'Se désinscrire'
          onPress={async () => {
            const { data: { leaveEvent: status } } = await leaveEvent({
              variables: { eventId: Number(event.id) },
              refetchQueries: [FindManyEvents, 'findManyEvents'],
            });
            toast.show({ title: status.message });
          }}
        >
          <IconButton icon="account-minus" color="white" size={20} />
          <Text variant="bodyMedium" style={styles.mainActionButtonText}>Se désinscrire</Text>
        </TouchableOpacity>
      );
    }
    return (
      <View style={styles.mainActionContainer}>
        <View style={styles.mainActionButtonsRow}>
          {event.hasAlreadyJoined
          && isUserPending ? (
            <TouchableOpacity
              style={[styles.mainActionButton, { backgroundColor: theme.colors.error }]} // Use error color for 'Se désinscrire'
              onPress={async () => {
                const { data: { leaveEvent: status } } = await leaveEvent({
                  variables: { eventId: Number(event.id) },
                  refetchQueries: [FindManyEvents, 'findManyEvents'],
                });
                toast.show({ title: status.message });
              }}
            >
              <IconButton icon="account-minus" color="white" size={20} />
              <Text variant="bodyMedium" style={styles.mainActionButtonText}>Se désinscrire</Text>
            </TouchableOpacity>
            ) : isUserConfirmed
              ? (
                <TouchableOpacity
                  style={[
                    styles.mainActionButton,
                    { backgroundColor: theme.colors.disabled },
                  ]}
                  disabled
                >
                  <IconButton icon="check-circle" color="white" size={20} />
                  <Text
                    variant="bodyMedium"
                    style={[styles.mainActionButtonText]}
                  >
                    Ton inscription a été validée
                  </Text>
                </TouchableOpacity>
              )
              : user?.status !== 'COMPANION' && (
                <TouchableOpacity
                  style={[
                    styles.mainActionButton,
                    { backgroundColor: !event.canJoin || isRegistrationClosed ? theme.colors.disabled : theme.colors.primary },
                  ]}
                  disabled={!event.canJoin || isRegistrationClosed}
                  onPress={async () => {
                    const { data: { joinEvent: status } } = await joinEvent({
                      variables: { joinEventId: Number(event.id) },
                      refetchQueries: [FindManyEvents, 'findManyEvents'],
                    });
                    toast.show({ title: status.message });
                  }}
                >
                  <IconButton icon="account-plus" color="white" size={20} />
                  <Text
                    variant="bodyMedium"
                    style={[styles.mainActionButtonText]}
                  >
                    S'inscrire
                  </Text>
                </TouchableOpacity>
              )}
        </View>
        <View style={styles.mainActionButtonsRow}>
          <TouchableOpacity
            style={[
              styles.mainActionButton,
              styles.participantCountContainer,
              { backgroundColor: isSuperAdmin ? theme.colors.error : theme.colors.secondary },
            ]}
            onPress={() => navigation.navigate({ name: SCREEN_NAMES.EVENT, params: { eventId: event.id } })}
          >
            <IconButton
              icon={isSuperAdmin ? 'application-edit' : 'eye'}
            />
            <Text variant="bodyMedium" style={styles.participantCountText}>{isSuperAdmin ? 'Modifier' : 'Détails'}</Text>
          </TouchableOpacity>
        </View>
        {user?.status === 'COMPANION' && (
          <View style={[styles.mainActionButtonsRow, styles.registrationWarningContainer]}>
            <IconButton icon="account-heart" size={20} iconColor={theme.colors.secondary} />
            <Text style={styles.registrationWarningText}>
              Les accompagnateurs ne participent pas aux compétitions.
            </Text>
          </View>
        )}
        {blockers.length > 0 && (
          <View style={[styles.mainActionButtonsRow, styles.registrationWarningContainer]}>
            <IconButton
              icon="message-alert"
              iconColor={blockers[0].code === 'REVIEW_PENDING'
                ? theme.colors.warning : theme.colors.error}
              size={20}
              onPress={() => setBlockersVisible(true)}
            />
            <Text style={styles.registrationWarningText} onPress={() => setBlockersVisible(true)}>
              {`${blockers[0].message}${blockers.length > 1 ? ` (+${blockers.length - 1})` : ''}`}
            </Text>
          </View>
        )}
        <Portal>
          <Dialog visible={blockersVisible} onDismiss={() => setBlockersVisible(false)}>
            <Dialog.Title>Pourquoi je ne peux pas m&apos;inscrire ?</Dialog.Title>
            <Dialog.Content>
              {blockers.map((b) => (
                <View key={b.code} style={{ flexDirection: 'row', marginBottom: 8 }}>
                  <IconButton
                    icon="alert-circle-outline"
                    iconColor={theme.colors.error}
                    size={20}
                    style={{ margin: 0 }}
                  />
                  <Text style={{ flex: 1 }}>{b.message}</Text>
                </View>
              ))}
            </Dialog.Content>
            <Dialog.Actions>
              {blockerActions.has('DOCUMENTS') && (
                <Button onPress={() => goToProfile(1)}>Mes documents</Button>
              )}
              {blockerActions.has('PROFILE') && (
                <Button onPress={() => goToProfile(0)}>Mon profil</Button>
              )}
              {blockerActions.has('WEIGHT') && (
                <Button
                  onPress={() => {
                    setBlockersVisible(false);
                    setWeightRequested(true);
                    toggleWeightModal();
                  }}
                >
                  Ajouter mon poids
                </Button>
              )}
              <Button onPress={() => setBlockersVisible(false)}>Fermer</Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>
        {weightRequested && displayWeightModal.display && <WeightTrackerModal />}
      </View>
    );
  };

  return (
    <Animated.View style={[
      styles.cardContainer,
      isDesktop && desktopStyles.cardContainer,
      event.lessThan14days && canStillRegister && registrationWarning,
      event.isTerminated,
    ]}
    >
      <Card
        elevation={2}
        style={[styles.card, isDesktop && desktopStyles.card]}
        contentStyle={isDesktop ? desktopStyles.cardInner : undefined}
      >
        <TouchableOpacity onPress={() => navigation.navigate({ name: SCREEN_NAMES.EVENT, params: { eventId: event.id } })}>
          <Card.Cover source={{ uri: event.mediaThumbnail }} style={[styles.cardCover, isDesktop && desktopStyles.cardCover]} />
          <View style={styles.coverOverlay}>
            {renderStatusChip()}
          </View>
          <IconButton
            icon="magnify"
            size={20}
            iconColor="white"
            containerColor="rgba(0, 0, 0, 0.5)"
            style={styles.expandImageButton}
            onPress={() => togglePictureModal({ eventId: event.id, isInfoMedia: false })}
          />
        </TouchableOpacity>
        <Card.Content style={[styles.cardContent, isDesktop && desktopStyles.cardContent]}>
          <View style={[styles.header, isDesktop && desktopStyles.header]}>
            <View style={styles.headerText}>
              <Text
                variant="headlineSmall"
                style={styles.title}
                numberOfLines={isDesktop ? 2 : undefined}
                ellipsizeMode="tail"
              >
                {event.name}
              </Text>
            </View>
            {countryCode && <Flag type="flat" code={countryCode} size={32} />}
          </View>

          <View style={[styles.infoSection, isDesktop && desktopStyles.infoSection]}>
            <View style={styles.infoRow}>
              <IconButton icon="calendar" size={20} style={styles.icon} />
              <Text variant="bodyMedium">
                {format(new Date(event.startAt), 'd', { locale: fr })}
                {format(new Date(event.startAt), 'd', { locale: fr }) !== format(new Date(event.endAt), 'd', { locale: fr })
                  ? ` - ${format(new Date(event.endAt), 'd', { locale: fr })}`
                  : ''}
                {format(new Date(event.endAt), ' MMMM yyyy', { locale: fr })}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <IconButton icon="map-marker" size={20} style={styles.icon} />
              <Text variant="bodyMedium" style={styles.locationText}>{event.location}</Text>
            </View>
            {event.categories?.length > 0 && (
              <View style={styles.infoRow}>
                <IconButton icon="tag-multiple" size={20} style={styles.icon} />
                <Text variant="bodyMedium">{event.categories.join(', ')}</Text>
              </View>
            )}
            {event.infoMediaThumbnail && (
              <View style={styles.infoRow}>
                <IconButton
                  icon="information-outline"
                  size={20}
                  style={styles.icon}
                  onPress={() => togglePictureModal({ eventId: event.id, isInfoMedia: true })}
                />
                <Text variant="bodyMedium">Information supplémentaire</Text>

              </View>
            )}
            {event.hasAssignedTrip && (
              <View style={styles.infoRow}>
                <IconButton icon="car" size={20} style={styles.icon} />
                <Chip
                  icon="check-circle"
                  style={{ backgroundColor: '#e8f5e9' }}
                  textStyle={{ color: '#2e7d32' }}
                >
                  Transport assigné
                </Chip>
              </View>
            )}
          </View>

          <Divider style={[styles.divider, isDesktop && desktopStyles.divider]} />

          <View style={styles.participantsSection}>
            <View style={styles.participantCountWrapper}>
              <TouchableOpacity
                style={[
                  styles.participantCountContainer,
                  {
                    backgroundColor: (event.players.filter((p) => p.isConfirmed).length === 0 || event.isTerminated)
                      ? theme.colors.disabled : theme.colors.success,
                  },
                ]}
                disabled={!(isSuperAdmin || isAdmin) || event.players.filter((p) => p.isConfirmed).length === 0 || event.isTerminated}
                onPress={() => handlePlayersOnEvent(event.id, 'REMOVE')}
              >
                <IconButton icon="account-check" size={20} />
                <Text variant="bodyMedium" style={styles.participantCountText}>
                  {`${event.players.filter((p) => p.isConfirmed).length} confirmé(s)`}
                </Text>
              </TouchableOpacity>
              {isSuperAdmin && !event.isTerminated && (
                <IconButton
                  icon="plus"
                  size={14}
                  mode="contained"
                  containerColor={theme.colors.primary}
                  iconColor="white"
                  style={styles.participantAddBadge}
                  onPress={() => handlePlayersOnEvent(event.id, 'ADD_PLAYER')}
                />
              )}
            </View>
            <TouchableOpacity
              style={[
                styles.participantCountContainer,
                {
                  backgroundColor: (event.players.filter((p) => !p.isConfirmed).length === 0 || event.isTerminated)
                    ? theme.colors.disabled : theme.colors.warning,
                },
              ]}
              disabled={!(isSuperAdmin || isAdmin) || event.players.filter((p) => !p.isConfirmed).length === 0 || event.isTerminated}
              onPress={() => handlePlayersOnEvent(event.id, 'ADD')}
            >
              <IconButton icon="account-clock" size={20} />
              <Text variant="bodyMedium" style={styles.participantCountText}>
                {`${event.players.filter((p) => !p.isConfirmed).length} en attente`}
              </Text>
            </TouchableOpacity>
          </View>
        </Card.Content>
        <Card.Actions>
          <View style={styles.mainActions}>
            {renderMainAction()}
          </View>
        </Card.Actions>
      </Card>
    </Animated.View>
  );
}

const desktopStyles = StyleSheet.create({
  cardContainer: {
    marginHorizontal: 0,
    flex: 1,
  },
  card: {
    flex: 1,
  },
  cardInner: {
    flex: 1,
  },
  cardCover: {
    height: 120,
  },
  cardContent: {
    paddingTop: 12,
    flexGrow: 1,
  },
  header: {
    minHeight: 64,
  },
  infoSection: {
    gap: 6,
    minHeight: 220,
  },
  divider: {
    marginVertical: 8,
  },
});

const styles = StyleSheet.create({
  cardContainer: {
    marginVertical: 8,
    marginHorizontal: 16,
    borderRadius: 12,
    overflow: 'hidden',
  },
  card: {
    borderRadius: 12,
    backgroundColor: 'white',
  },
  cardCover: {
    height: 150,
  },
  coverOverlay: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  expandImageButton: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    margin: 0,
  },
  statusChip: {
    elevation: 2,
  },
  cardContent: {
    paddingTop: 16,
    paddingBottom: 0,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
  },
  infoSection: {
    gap: 8,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    margin: 0,
    marginRight: 8,
  },
  locationText: {
    flexShrink: 1,
  },
  divider: {
    marginVertical: 12,
  },
  participantsSection: {
    flexDirection: 'row', // Revert to row to place them side-by-side
    justifyContent: 'space-between', // Distribute items with space between them
    gap: 8, // Keep gap for spacing between stacked items
  },
  participantChip: {
    // styles for participant chips if needed
  },
  participantCountContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8, // Increased padding for better touch area
    paddingHorizontal: 12, // Increased padding
    borderRadius: 8, // Slightly less rounded corners
    backgroundColor: '#f0f0f0', // A light background to make it look like a chip
    flex: 1, // Allow items to grow and shrink
    justifyContent: 'center', // Center content horizontally
  },
  participantCountWrapper: {
    flex: 1,
    position: 'relative',
  },
  participantAddBadge: {
    position: 'absolute',
    top: -8,
    right: -8,
    margin: 0,
    width: 24,
    height: 24,
  },
  participantCountText: {
    marginLeft: 4, // Adjust to bring text closer to icon
    fontWeight: 'bold',
  },
  mainActions: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  mainActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  mainActionButtonText: {
    fontWeight: 'bold',
    marginLeft: -4, // Adjust to bring text closer to icon
  },
  registrationWarningContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: '#fff3cd', // Light yellow background for warning
    marginBottom: 8,
  },
  registrationWarningText: {
    color: '#856404', // Dark yellow text for warning
    marginLeft: 4,
    flexShrink: 1,
  },
  mainActionContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  mainActionButtonsRow: {
    width: '100%',
  },
});

export default EventCard;
